import { Badge } from "@/components/ui/Badge";
import * as styles from "@/pages/InvoiceDetailPage.styles";
import * as shared from "@/styles/shared";
import type { InvoicePaymentAttempt } from "@shared/types/payment";
import { PaymentResultStatus } from "@shared/types/payment";
import { formatAmount, formatDate } from "@shared/utils";
import { AlertTriangle, CheckCircle2, Clock, History } from "lucide-react";
import { useTranslation } from "react-i18next";

interface InvoicePaymentHistoryProps {
  attempts: InvoicePaymentAttempt[];
}

const resultMeta = {
  [PaymentResultStatus.SUCCESS]: { labelKey: "resultSuccess", icon: CheckCircle2, className: styles.attemptSuccess },
  [PaymentResultStatus.FAILURE]: { labelKey: "resultFailure", icon: AlertTriangle, className: styles.attemptFailure },
  [PaymentResultStatus.PENDING]: { labelKey: "resultPending", icon: Clock, className: styles.attemptPending },
} as const;

const InvoicePaymentHistory = ({ attempts }: InvoicePaymentHistoryProps) => {
  const { t } = useTranslation("invoiceDetail");

  if (!attempts.length) {
    return (
      <div className={styles.emptyPanel}>
        <History className={styles.emptyPanelIcon} />
        <p className={shared.muted}>{t("paymentHistoryEmpty", { ns: "invoiceDetail" })}</p>
      </div>
    );
  }

  return (
    <ol className={styles.timeline}>
      {attempts.map((attempt) => {
        const meta = resultMeta[attempt.success] ?? resultMeta[PaymentResultStatus.PENDING];
        const ResultIcon = meta.icon;

        return (
          <li key={attempt.id} className={styles.timelineItem}>
            <div className={`${styles.timelineMarker} ${meta.className}`}>
              <ResultIcon className={styles.timelineMarkerIcon} />
            </div>

            <div className={styles.timelineBody}>
              <div className={styles.timelineHeader}>
                <span className={shared.titleSmSemibold}>
                  {t("attemptedAt", { ns: "invoiceDetail" })} {formatDate(attempt.createdAt)}
                </span>
                <Badge variant={attempt.success === PaymentResultStatus.SUCCESS ? "success" : "destructive"}>
                  {t(meta.labelKey, { ns: "invoiceDetail" })}
                </Badge>
              </div>

              <div className={styles.timelineMeta}>
                <span>{t("attemptRun", { ns: "invoiceDetail", id: attempt.paymentLogId })}</span>
                <span aria-hidden="true">·</span>
                <span>{t("runStatusLabel." + attempt.paymentLog.status, { ns: "invoiceDetail" })}</span>
                <span aria-hidden="true">·</span>
                <span>
                  {t("attemptSource", { ns: "invoiceDetail" })}: {t(`sourceLabel.${attempt.paymentLog.source}`, { ns: "invoiceDetail" })}
                </span>
                <span aria-hidden="true">·</span>
                <span>{formatAmount(attempt.amount)}</span>
              </div>

              {attempt.paymentLog.errorMessage && (
                <p className={styles.attemptError}>
                  <AlertTriangle className={styles.attemptErrorIcon} />
                  {attempt.paymentLog.errorMessage}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export { InvoicePaymentHistory, type InvoicePaymentHistoryProps };
